/**
 * Generation number calculation utilities
 * Shared logic for calculating generation numbers in both flowchart and sidebar
 */

export interface GenerationJob {
  job_id: string;
  base_job_id: string | null;
}

/**
 * Calculate generation numbers for a list of jobs using BFS algorithm
 * Generation 1 = root jobs (no base_job_id or base_job_id not in the list)
 * Generation N = children of generation N-1 jobs
 * 
 * @param jobs Array of jobs with job_id and base_job_id
 * @returns Map of job_id -> generation number
 */
export function calculateGenerationNumbers(jobs: GenerationJob[]): Map<string, number> {
  if (!jobs || jobs.length === 0) {
    return new Map();
  }

  // Create a map for quick lookup
  const jobMap = new Map<string, GenerationJob>();
  jobs.forEach(job => {
    jobMap.set(job.job_id, job);
  });

  // Find root jobs (those without base_job_id or whose base_job_id is not in the list)
  const rootJobs = jobs.filter(job => !job.base_job_id || !jobMap.has(job.base_job_id));

  // Build generation numbers (distance from root)
  const generationNumbers = new Map<string, number>();
  const visited = new Set<string>();

  // BFS to assign generation numbers
  const queue: { job: GenerationJob; generation: number }[] = [];
  rootJobs.forEach(job => {
    queue.push({ job, generation: 1 });
    generationNumbers.set(job.job_id, 1);
  });

  while (queue.length > 0) {
    const { job, generation } = queue.shift()!;
    if (visited.has(job.job_id)) continue;
    visited.add(job.job_id);

    // Find children (jobs that have this job as their base_job_id)
    const children = jobs.filter(j => j.base_job_id === job.job_id);
    children.forEach(child => {
      if (!generationNumbers.has(child.job_id)) {
        generationNumbers.set(child.job_id, generation + 1);
        queue.push({ job: child, generation: generation + 1 });
      }
    });
  }

  return generationNumbers;
}